/**
 * THE BOARD - what the table knows, written down.
 *
 * A lead, a name, a place, or a line of the story the GM pinned so it does
 * not scroll away. Each entry is a title and a body; like a clock it is the
 * GM's alone until shown.
 *
 * Every function returns a new entry. Pure, zero React dependency.
 */
import { audienceOf, readShownBase } from './shown';
import type { Shown } from './shown';

const MAX_TITLE = 80;
const MAX_TEXT = 2000;

export interface BoardEntry extends Shown {
  title: string;
  text: string;
}

const cleanTitle = (title: string) => title.trim().slice(0, MAX_TITLE);
const cleanText = (text: string) => text.trim().slice(0, MAX_TEXT);

/** A new entry, or null when it would say nothing at all. */
export function newEntry(args: {
  id: string;
  title: string;
  text: string;
  createdAt: number;
  visible?: boolean;
}): BoardEntry | null {
  const title = cleanTitle(args.title);
  const text = cleanText(args.text);
  if (!title && !text) return null;
  const audience = audienceOf({ visible: args.visible === true }, []);
  return { id: args.id, title, text, audience, visible: audience.length > 0, createdAt: args.createdAt };
}

/** Rewrite an entry, or show or keep it. An edit that would empty it is refused (null): delete it instead. */
export function editEntry(
  entry: BoardEntry,
  patch: Partial<Pick<BoardEntry, 'title' | 'text' | 'visible' | 'audience'>>,
): BoardEntry | null {
  const title = patch.title === undefined ? entry.title : cleanTitle(patch.title);
  const text = patch.text === undefined ? entry.text : cleanText(patch.text);
  if (!title && !text) return null;
  const audience = audienceOf(patch, entry.audience);
  return { ...entry, title, text, audience, visible: audience.length > 0 };
}

/**
 * A line of the story, pinned: the line is the body, under no title. Shown
 * at once - it was read from a log the table already shares.
 */
export function pinnedLine(id: string, line: string, createdAt: number): BoardEntry | null {
  return newEntry({ id, title: '', text: line, createdAt, visible: true });
}

/** What a pin needs to know of a vox line. */
interface SpokenLine {
  kind: string;
  text: string;
  speakAs: { name: string } | null;
  fromName: string;
}

/**
 * A vox line as the sentence a pin keeps: who said it and what, or the
 * emote as it reads. Null for what is not a line of the story - a roll, a
 * handout, a removed message - and for a line with no words.
 */
export function pinTextOf(line: SpokenLine): string | null {
  const text = line.text.trim();
  const who = line.speakAs?.name || line.fromName || '?';
  if (!text) return null;
  if (line.kind === 'say') return `${who}: ${text}`;
  if (line.kind === 'emote') return `${who} ${text}`;
  // The GM's announcement (v9.7.0): the words alone, as they were said to the table.
  if (line.kind === 'system') return text;
  return null;
}

/** An entry that crossed the wire, validated; null when it is not one. */
export function readEntry(id: string, raw: unknown): BoardEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const stored = raw as Record<string, unknown>;
  const title = typeof stored.title === 'string' ? cleanTitle(stored.title) : '';
  const text = typeof stored.text === 'string' ? cleanText(stored.text) : '';
  if (!title && !text) return null;
  return { ...readShownBase(id, stored), title, text };
}
